import {
  ArrowUpTrayIcon,
  DocumentIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';

import { cn } from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

interface FileDropzoneProps {
  label: string;
  files: File[];
  onChange: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  error?: string;
  required?: boolean;
}

/**
 * Self-contained drag-and-drop file input using Tailwind / HeroUI design tokens.
 * Manages its own drag state; file state is lifted via `onChange`.
 */
const FileDropzone: React.FC<FileDropzoneProps> = ({
  label,
  files,
  onChange,
  accept = 'image/*,.pdf,.doc,.docx',
  multiple = true,
  error,
  required = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: FileList | File[]) => {
    const newFiles = Array.from(incoming);

    onChange(multiple ? [...files, ...newFiles] : newFiles);
  };

  const removeFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
    // Reset so the same file can be re-selected if removed
    e.target.value = '';
  };

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="body-2" className="text-secondary-text">
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </Typography>

      {/* Drop target */}
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed p-6 cursor-pointer transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-gray-4 bg-gray-1 hover:bg-gray-2'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      >
        <div className="size-10 rounded-full bg-gray-3 flex items-center justify-center">
          <ArrowUpTrayIcon className="size-5 text-gray-600" />
        </div>
        <div className="text-center">
          <Typography variant="body-2" weight="medium">
            Click to upload or drag & drop
          </Typography>
          <Typography variant="body-2" className="text-secondary-text">
            PDF, DOC, DOCX, images supported
          </Typography>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <ul className="flex flex-col gap-2 mt-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="flex items-center gap-3 rounded-lg border border-gray-3 bg-white px-3 py-2"
            >
              <DocumentIcon className="size-5 text-gray-500 flex-shrink-0" />
              <Typography variant="body-2" className="flex-1 truncate">
                {file.name}
              </Typography>
              <Typography
                variant="body-2"
                className="text-secondary-text flex-shrink-0"
              >
                {(file.size / 1024).toFixed(0)} KB
              </Typography>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                className="flex-shrink-0 p-1 hover:bg-gray-2 rounded-full transition-colors"
                aria-label={`Remove ${file.name}`}
              >
                <XMarkIcon className="size-4 text-gray-600" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <Typography variant="body-2" className="text-danger">
          {error}
        </Typography>
      )}
    </div>
  );
};

export default FileDropzone;
